/**
 * Reminders domain — React Query hooks.
 *
 * The generated `listReminders` response is wrapped in DRF's auto-
 * pagination shape (`PaginatedReminderListResponseList`), but the
 * runtime actually returns the manually-built dict from
 * `GetRemindersListUsecase` (`ReminderListResponse` — `count`, `page`,
 * `page_size`, `total_pages`, `results`). We hand-write the list hook
 * to type the response correctly. Same pattern as
 * `useListVariantsCatalog` in `services/products`.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { listReminders } from '../generated/clients/listReminders'
import { createReminder } from '../generated/clients/createReminder'
import { updateReminder } from '../generated/clients/updateReminder'
import { partialUpdateReminder } from '../generated/clients/partialUpdateReminder'
import { deleteReminder } from '../generated/clients/deleteReminder'
import { pauseReminder } from '../generated/clients/pauseReminder'
import { unpauseReminder } from '../generated/clients/unpauseReminder'
import { listRemindersQueryKey } from '../generated/hooks/remindersController'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

import type { ListRemindersQueryParams } from '../generated/types/ListReminders'
import type { CreateReminderMutationRequest } from '../generated/types/CreateReminder'
import type {
  UpdateReminderMutationRequest,
  UpdateReminderPathParams,
} from '../generated/types/UpdateReminder'
import type {
  PartialUpdateReminderMutationRequest,
  PartialUpdateReminderPathParams,
} from '../generated/types/PartialUpdateReminder'
import type { DeleteReminderPathParams } from '../generated/types/DeleteReminder'
import type { PauseReminderPathParams } from '../generated/types/PauseReminder'
import type { UnpauseReminderPathParams } from '../generated/types/UnpauseReminder'
import type { ReminderListResponse } from './utils'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useGetReminderDetail,
  getReminderDetailQueryKey,
  listRemindersQueryKey,
} from '../generated/hooks/remindersController'

// ── Generated query/mutation parameter & response type re-exports ──

export type {
  ListRemindersQueryParams,
  ListRemindersQueryResponse,
} from '../generated/types/ListReminders'
export type {
  GetReminderDetailQueryResponse,
  GetReminderDetailPathParams,
} from '../generated/types/GetReminderDetail'
export type {
  CreateReminderMutationRequest,
  CreateReminderMutationResponse,
} from '../generated/types/CreateReminder'
export type {
  UpdateReminderMutationRequest,
  UpdateReminderMutationResponse,
  UpdateReminderPathParams,
} from '../generated/types/UpdateReminder'
export type {
  PartialUpdateReminderMutationRequest,
  PartialUpdateReminderMutationResponse,
  PartialUpdateReminderPathParams,
} from '../generated/types/PartialUpdateReminder'
export type {
  DeleteReminderMutationResponse,
  DeleteReminderPathParams,
} from '../generated/types/DeleteReminder'
export type {
  PauseReminderMutationResponse,
  PauseReminderPathParams,
} from '../generated/types/PauseReminder'
export type {
  UnpauseReminderMutationResponse,
  UnpauseReminderPathParams,
} from '../generated/types/UnpauseReminder'

// ── Hand-written query hooks ───────────────────────────────────────

/**
 * List reminders with filtering and pagination.
 *
 * Hand-written wrapper around the generated `listReminders` client
 * function because drf-spectacular wraps `ListAPIView`-style responses
 * in DRF's auto-pagination shape, but the actual runtime returns the
 * manual paginated dict built by `GetRemindersListUsecase`. This wrapper
 * casts the response to the correct `ReminderListResponse` shape.
 */
export function useListReminders(
  params?: ListRemindersQueryParams,
  options: {
    query?: Omit<
      UseQueryOptions<ReminderListResponse, Error>,
      'queryKey' | 'queryFn'
    >
  } = {}
) {
  const { query = {} } = options
  return useQuery<ReminderListResponse, Error>({
    queryKey: listRemindersQueryKey(params),
    queryFn: async ({ signal }) =>
      (await listReminders(params, {
        signal,
      })) as unknown as ReminderListResponse,
    ...query,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useCreateReminder() {
  return useInvalidatingMutation('createReminder', invalidationRegistry, {
    mutationFn: (variables: { data: CreateReminderMutationRequest }) =>
      createReminder(variables.data),
  })
}

export function useUpdateReminder() {
  return useInvalidatingMutation('updateReminder', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateReminderPathParams['id']
      data: UpdateReminderMutationRequest
    }) => updateReminder({ id: variables.id }, variables.data),
  })
}

export function usePartialUpdateReminder() {
  return useInvalidatingMutation(
    'partialUpdateReminder',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        id: PartialUpdateReminderPathParams['id']
        data: PartialUpdateReminderMutationRequest
      }) => partialUpdateReminder({ id: variables.id }, variables.data),
    }
  )
}

export function useDeleteReminder() {
  return useInvalidatingMutation('deleteReminder', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteReminderPathParams['id'] }) =>
      deleteReminder({ id: variables.id }),
  })
}

export function usePauseReminder() {
  return useInvalidatingMutation('pauseReminder', invalidationRegistry, {
    mutationFn: (variables: { id: PauseReminderPathParams['id'] }) =>
      pauseReminder({ id: variables.id }),
  })
}

export function useUnpauseReminder() {
  return useInvalidatingMutation('unpauseReminder', invalidationRegistry, {
    mutationFn: (variables: { id: UnpauseReminderPathParams['id'] }) =>
      unpauseReminder({ id: variables.id }),
  })
}
